// src/pages/GeriBildirimlerPage.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  DeleteButton,
  Edit,
  ExportButton,
  ImageField,
  List,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar as RaToolbar,
  useDataProvider,
  useListContext,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useUpdate,
} from "react-admin";
import GlobalStyles from "@mui/material/GlobalStyles";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField as TextFieldMUI,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { AnimatePresence, motion } from "framer-motion";

const MotionPaper = motion(Paper);
const MotionBox = motion(Box);

const COLORS = {
  navy: "#071a33",
  navySoft: "#12375f",
  blue: "#2563eb",
  blueSoft: "#eff6ff",
  greenDark: "#047857",
  greenSoft: "#ecfdf5",
  orange: "#f59e0b",
  orangeSoft: "#fff7ed",
  redDark: "#b91c1c",
  redSoft: "#fff1f2",
  violet: "#7c3aed",
  text: "#0f172a",
  textSoft: "#64748b",
  page: "#f1f5f9",
  border: "#e2e8f0",
};

const ALLOWED = ["tr", "en", "de"];

const T = {
  tr: {
    pageTitle: "Geri Bildirim Merkezi",
    pageSubtitle:
      "İstek, öneri ve şikayetleri tek merkezden takip edin; durumları yönetin ve hızlıca yanıtlayın.",
    detailTitle: "Geri Bildirim Detayı",
    detailSubtitle:
      "Başvuru bilgilerini inceleyin, durumunu güncelleyin ve yanıt geçmişini yönetin.",
    overview: "Genel görünüm",
    autoRefresh: "10 sn otomatik yenileme",
    filters: {
      durum: "Durum filtresi",
    },
    fields: {
      sender: "Gönderen",
      category: "Kategori",
      title: "Başlık",
      desc: "Açıklama",
      image: "Ekli Görsel",
      status: "Durum",
    },
    statuses: {
      yeni: "Yeni",
      inceleniyor: "İnceleniyor",
      cozuldu: "Çözüldü",
    },
    categories: {
      sikayet: "Şikayet",
      oneri: "Öneri",
      istek: "İstek",
    },
    toastUpdated: "✅ Durum güncellendi",
    errorGeneric:
      "Güncelleme sırasında bir hata oluştu.",
    quick: {
      all: "Tümü",
    },
    actions: {
      back: "Geri Dön",
      save: "Değişiklikleri Kaydet",
      openDetail: "Detayı Aç",
      send: "Yanıt Gönder",
      export: "Dışa Aktar",
    },
    stats: {
      total: "Toplam Kayıt",
      new: "Yeni",
      inreview: "İncelemede",
      resolved: "Çözüldü",
    },
    empty: {
      title: "Kayıt bulunamadı",
      hint: "Seçili filtreyi değiştirip tekrar deneyin.",
      noImage: "Bu kayda görsel eklenmemiş.",
    },
    sections: {
      wishes: "İstek ve Öneriler",
      complaints: "Şikayetler",
    },
    table: {
      subject: "Başvuru",
      message: "Açıklama",
      status: "Durum",
      date: "Tarih",
      action: "İşlem",
    },
    replies: {
      title: "Yanıt Geçmişi",
      subtitle:
        "Başvuru sahibiyle yapılan görüşmeler",
      placeholder:
        "Kurumsal yanıtınızı yazın...",
      loading: "Yanıtlar yükleniyor…",
      none: "Henüz yanıt gönderilmedi.",
      delete: "Kaydı Sil",
      deleteConfirmTitle: "Silme Onayı",
      deleteConfirmContent:
        "Bu geri bildirimi silmek istediğinize emin misiniz?",
      sentOk: "✅ Yanıt gönderildi",
      sendErr: "❌ Yanıt gönderilemedi",
    },
  },

  en: {
    pageTitle: "Feedback Center",
    pageSubtitle:
      "Track requests, suggestions and complaints in one place; manage statuses and respond quickly.",
    detailTitle: "Feedback Detail",
    detailSubtitle:
      "Review the submission, update its status and manage the reply history.",
    overview: "Overview",
    autoRefresh: "Auto-refresh every 10 sec",
    filters: {
      durum: "Status filter",
    },
    fields: {
      sender: "Sender",
      category: "Category",
      title: "Title",
      desc: "Description",
      image: "Attached Image",
      status: "Status",
    },
    statuses: {
      yeni: "New",
      inceleniyor: "In Review",
      cozuldu: "Resolved",
    },
    categories: {
      sikayet: "Complaint",
      oneri: "Suggestion",
      istek: "Request",
    },
    toastUpdated: "✅ Status updated",
    errorGeneric:
      "An error occurred while updating.",
    quick: {
      all: "All",
    },
    actions: {
      back: "Back",
      save: "Save Changes",
      openDetail: "Open Detail",
      send: "Send Reply",
      export: "Export",
    },
    stats: {
      total: "Total Records",
      new: "New",
      inreview: "In Review",
      resolved: "Resolved",
    },
    empty: {
      title: "No records found",
      hint: "Change the selected filter and try again.",
      noImage: "No image was attached to this record.",
    },
    sections: {
      wishes: "Requests and Suggestions",
      complaints: "Complaints",
    },
    table: {
      subject: "Submission",
      message: "Description",
      status: "Status",
      date: "Date",
      action: "Action",
    },
    replies: {
      title: "Reply History",
      subtitle:
        "Conversations with the submitter",
      placeholder:
        "Write your official reply...",
      loading: "Loading replies…",
      none: "No replies have been sent yet.",
      delete: "Delete Record",
      deleteConfirmTitle:
        "Delete Confirmation",
      deleteConfirmContent:
        "Are you sure you want to delete this feedback?",
      sentOk: "✅ Reply sent",
      sendErr: "❌ Failed to send reply",
    },
  },

  de: {
    pageTitle: "Feedback-Zentrale",
    pageSubtitle:
      "Anfragen, Vorschläge und Beschwerden zentral verfolgen, verwalten und beantworten.",
    detailTitle: "Feedback-Details",
    detailSubtitle:
      "Einreichung prüfen, Status aktualisieren und Antwortverlauf verwalten.",
    overview: "Übersicht",
    autoRefresh:
      "Automatische Aktualisierung alle 10 Sek.",
    filters: {
      durum: "Statusfilter",
    },
    fields: {
      sender: "Absender",
      category: "Kategorie",
      title: "Titel",
      desc: "Beschreibung",
      image: "Angehängtes Bild",
      status: "Status",
    },
    statuses: {
      yeni: "Neu",
      inceleniyor: "In Prüfung",
      cozuldu: "Gelöst",
    },
    categories: {
      sikayet: "Beschwerde",
      oneri: "Vorschlag",
      istek: "Anfrage",
    },
    toastUpdated: "✅ Status aktualisiert",
    errorGeneric:
      "Beim Aktualisieren ist ein Fehler aufgetreten.",
    quick: {
      all: "Alle",
    },
    actions: {
      back: "Zurück",
      save: "Änderungen speichern",
      openDetail: "Details öffnen",
      send: "Antwort senden",
      export: "Exportieren",
    },
    stats: {
      total: "Gesamt",
      new: "Neu",
      inreview: "In Prüfung",
      resolved: "Gelöst",
    },
    empty: {
      title: "Keine Einträge gefunden",
      hint: "Ändern Sie den Filter und versuchen Sie es erneut.",
      noImage:
        "Diesem Eintrag wurde kein Bild hinzugefügt.",
    },
    sections: {
      wishes: "Anfragen und Vorschläge",
      complaints: "Beschwerden",
    },
    table: {
      subject: "Einreichung",
      message: "Beschreibung",
      status: "Status",
      date: "Datum",
      action: "Aktion",
    },
    replies: {
      title: "Antwortverlauf",
      subtitle:
        "Gespräche mit dem Absender",
      placeholder:
        "Offizielle Antwort schreiben...",
      loading: "Antworten werden geladen…",
      none: "Noch keine Antworten gesendet.",
      delete: "Eintrag löschen",
      deleteConfirmTitle:
        "Löschbestätigung",
      deleteConfirmContent:
        "Möchten Sie dieses Feedback wirklich löschen?",
      sentOk: "✅ Antwort gesendet",
      sendErr:
        "❌ Antwort konnte nicht gesendet werden",
    },
  },
};

function useUiTexts() {
  const [uiLang, setUiLang] = useState(() =>
    (
      localStorage.getItem("app_lang") || "tr"
    )
      .slice(0, 2)
      .toLowerCase(),
  );

  useEffect(() => {
    const sync = () => {
      const nextLanguage = (
        localStorage.getItem("app_lang") ||
        "tr"
      )
        .slice(0, 2)
        .toLowerCase();

      setUiLang(
        ALLOWED.includes(nextLanguage)
          ? nextLanguage
          : "tr",
      );
    };

    window.addEventListener(
      "app_lang_change",
      sync,
    );
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);

    return () => {
      window.removeEventListener(
        "app_lang_change",
        sync,
      );
      window.removeEventListener(
        "storage",
        sync,
      );
      window.removeEventListener("focus", sync);
    };
  }, []);

  return T[
    ALLOWED.includes(uiLang) ? uiLang : "tr"
  ];
}

function useChoices() {
  const TT = useUiTexts();

  const DURUMLAR = useMemo(
    () => [
      {
        id: "Yeni",
        name: TT.statuses.yeni,
      },
      {
        id: "İnceleniyor",
        name: TT.statuses.inceleniyor,
      },
      {
        id: "Çözüldü",
        name: TT.statuses.cozuldu,
      },
    ],
    [TT],
  );

  const KATEGORILER = useMemo(
    () => [
      {
        id: "Şikayet",
        name: TT.categories.sikayet,
      },
      {
        id: "Öneri",
        name: TT.categories.oneri,
      },
      {
        id: "İstek",
        name: TT.categories.istek,
      },
    ],
    [TT],
  );

  return {
    DURUMLAR,
    KATEGORILER,
    TT,
  };
}

function getStatusMeta(status, TT) {
  if (status === "Çözüldü") {
    return {
      label: TT.statuses.cozuldu,
      color: COLORS.greenDark,
      bg: COLORS.greenSoft,
      border: "#a7f3d0",
      icon: <TaskAltIcon fontSize="small" />,
    };
  }

  if (status === "İnceleniyor") {
    return {
      label: TT.statuses.inceleniyor,
      color: "#b45309",
      bg: COLORS.orangeSoft,
      border: "#fed7aa",
      icon: (
        <HourglassTopIcon fontSize="small" />
      ),
    };
  }

  return {
    label: TT.statuses.yeni,
    color: COLORS.redDark,
    bg: COLORS.redSoft,
    border: "#fecdd3",
    icon: (
      <ReportProblemOutlinedIcon fontSize="small" />
    ),
  };
}

function getCategoryMeta(value, TT) {
  if (value === "Şikayet") {
    return {
      label: TT.categories.sikayet,
      color: COLORS.redDark,
      bg: COLORS.redSoft,
    };
  }

  if (value === "Öneri") {
    return {
      label: TT.categories.oneri,
      color: "#0369a1",
      bg: "#f0f9ff",
    };
  }

  if (value === "İstek") {
    return {
      label: TT.categories.istek,
      color: "#7c2d12",
      bg: COLORS.orangeSoft,
    };
  }

  return {
    label: value || "-",
    color: COLORS.textSoft,
    bg: "#f1f5f9",
  };
}

function StatusChip({
  value,
  TT,
  compact = false,
}) {
  const meta = getStatusMeta(value, TT);

  return (
    <Chip
      size="small"
      icon={meta.icon}
      label={meta.label}
      sx={{
        height: compact ? 28 : 32,
        fontWeight: 850,
        color: meta.color,
        bgcolor: meta.bg,
        border: `1px solid ${meta.border}`,

        "& .MuiChip-icon": {
          color: meta.color,
        },
      }}
    />
  );
}

function StatCard({
  title,
  value,
  icon,
  accent,
  active,
}) {
  return (
    <MotionPaper
      whileHover={{ y: -3 }}
      transition={{
        type: "spring",
        stiffness: 230,
        damping: 22,
      }}
      sx={{
        width: "100%",
        minHeight: {
          xs: 108,
          md: 124,
        },
        p: {
          xs: 1.45,
          sm: 1.8,
        },
        borderRadius: 3,
        color: "#fff",
        bgcolor: active
          ? "rgba(255,255,255,.16)"
          : "rgba(255,255,255,.09)",
        border: `1px solid ${
          active
            ? "rgba(255,255,255,.3)"
            : "rgba(255,255,255,.13)"
        }`,
        backdropFilter: "blur(12px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ color: accent }}>{icon}</Box>

      <Box>
        <Typography
          sx={{
            fontSize: {
              xs: "1.65rem",
              sm: "2rem",
            },
            lineHeight: 1,
            fontWeight: 950,
          }}
        >
          {value}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            mt: 0.65,
            display: "block",
            color: "rgba(255,255,255,.72)",
            fontWeight: 750,
          }}
        >
          {title}
        </Typography>
      </Box>
    </MotionPaper>
  );
}

function QuickFilters({
  value,
  onChange,
  TT,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: {
          xs: 1.25,
          sm: 1.5,
        },
        borderRadius: 3,
        borderColor: COLORS.border,
        bgcolor: "rgba(255,255,255,.96)",
        boxShadow:
          "0 10px 30px rgba(15,23,42,.06)",
      }}
    >
      <Stack
        direction={{
          xs: "column",
          lg: "row",
        }}
        spacing={1.25}
        alignItems={{
          xs: "stretch",
          lg: "center",
        }}
        justifyContent="space-between"
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2.25,
              display: "grid",
              placeItems: "center",
              color: COLORS.blue,
              bgcolor: COLORS.blueSoft,
            }}
          >
            <FilterAltOutlinedIcon fontSize="small" />
          </Box>

          <Typography
            sx={{
              fontWeight: 900,
              color: COLORS.text,
            }}
          >
            {TT.filters.durum}
          </Typography>
        </Stack>

        <Box
          sx={{
            overflowX: "auto",
            pb: 0.25,
          }}
        >
          <ToggleButtonGroup
            exclusive
            value={value}
            onChange={onChange}
            size="small"
            sx={{
              minWidth: "max-content",
              gap: 0.75,

              "& .MuiToggleButtonGroup-grouped": {
                mx: 0,
                border: `1px solid ${COLORS.border} !important`,
                borderRadius: "10px !important",
                px: {
                  xs: 1.3,
                  sm: 2,
                },
                py: 0.8,
                textTransform: "none",
                fontWeight: 850,
                color: COLORS.textSoft,
                bgcolor: "#fff",

                "&.Mui-selected": {
                  color: "#fff",
                  bgcolor: `${COLORS.navy} !important`,
                  borderColor: `${COLORS.navy} !important`,
                },
              },
            }}
          >
            <ToggleButton value="ALL">
              {TT.quick.all}
            </ToggleButton>

            <ToggleButton value="Yeni">
              {TT.statuses.yeni}
            </ToggleButton>

            <ToggleButton value="İnceleniyor">
              {TT.statuses.inceleniyor}
            </ToggleButton>

            <ToggleButton value="Çözüldü">
              {TT.statuses.cozuldu}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Stack>
    </Paper>
  );
}

function EmptyState() {
  const { TT } = useChoices();

  return (
    <MotionPaper
      initial={{
        opacity: 0,
        y: 12,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      variant="outlined"
      sx={{
        mx: {
          xs: 1,
          md: 3,
        },
        my: 3,
        p: {
          xs: 4,
          md: 6,
        },
        textAlign: "center",
        borderRadius: 4,
        borderStyle: "dashed",
        borderColor: "#cbd5e1",
        bgcolor: "#fff",
      }}
    >
      <Avatar
        sx={{
          width: 58,
          height: 58,
          mx: "auto",
          bgcolor: COLORS.blueSoft,
          color: COLORS.blue,
        }}
      >
        <ChatBubbleOutlineIcon />
      </Avatar>

      <Typography
        sx={{
          mt: 1.5,
          fontSize: "1.15rem",
          fontWeight: 900,
        }}
      >
        {TT.empty.title}
      </Typography>

      <Typography
        sx={{
          mt: 0.6,
          color: COLORS.textSoft,
        }}
      >
        {TT.empty.hint}
      </Typography>
    </MotionPaper>
  );
}

function ListHeader() {
  const {
    data = [],
    isLoading,
    isFetching,
    filterValues,
    setFilters,
  } = useListContext();

  const { TT } = useChoices();

  const counts = useMemo(() => {
    const result = {
      total: data.length,
      yeni: 0,
      inc: 0,
      coz: 0,
    };

    data.forEach((record) => {
      if (record.durum === "Yeni") {
        result.yeni += 1;
      } else if (
        record.durum === "İnceleniyor"
      ) {
        result.inc += 1;
      } else if (record.durum === "Çözüldü") {
        result.coz += 1;
      }
    });

    return result;
  }, [data]);

  const current =
    filterValues?.durum || "ALL";

  const handleQuick = (_, value) => {
    if (!value || value === "ALL") {
      setFilters(
        {
          ...filterValues,
          durum: undefined,
        },
        {},
      );
    } else {
      setFilters(
        {
          ...filterValues,
          durum: value,
        },
        {},
      );
    }
  };

  return (
    <Box>
      <Box
        sx={{
          color: "#fff",
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(125deg, #06172d 0%, #123d70 54%, #075985 100%)",
          borderBottom: `4px solid ${COLORS.orange}`,

          "&::before": {
            content: '""',
            position: "absolute",
            width: {
              xs: 260,
              md: 520,
            },
            height: {
              xs: 260,
              md: 520,
            },
            right: {
              xs: -160,
              md: -170,
            },
            top: {
              xs: -150,
              md: -300,
            },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,255,255,.17), transparent 68%)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            px: {
              xs: 1.5,
              sm: 2.5,
              md: 3.5,
            },
            pt: {
              xs: 3,
              md: 4,
            },
            pb: {
              xs: 3.5,
              md: 4,
            },
          }}
        >
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            alignItems={{
              xs: "stretch",
              md: "flex-start",
            }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box sx={{ maxWidth: 800 }}>
              <Chip
                icon={
                  <AssessmentOutlinedIcon />
                }
                label={TT.overview}
                sx={{
                  mb: 1.5,
                  color: "#fff",
                  fontWeight: 800,
                  bgcolor:
                    "rgba(255,255,255,.11)",
                  border:
                    "1px solid rgba(255,255,255,.17)",

                  "& .MuiChip-icon": {
                    color: "#67e8f9",
                  },
                }}
              />

              <Typography
                component="h1"
                sx={{
                  fontSize: {
                    xs: "1.9rem",
                    sm: "2.65rem",
                    md: "3.2rem",
                  },
                  lineHeight: 1.05,
                  fontWeight: 950,
                  letterSpacing: {
                    xs: -0.5,
                    md: -1.2,
                  },
                }}
              >
                {TT.pageTitle}
              </Typography>

              <Typography
                sx={{
                  mt: 1.2,
                  maxWidth: 720,
                  color:
                    "rgba(255,255,255,.74)",
                  lineHeight: 1.65,
                  fontSize: {
                    xs: ".93rem",
                    sm: "1.04rem",
                  },
                }}
              >
                {TT.pageSubtitle}
              </Typography>
            </Box>

            <Stack
              direction={{
                xs: "row",
                md: "column",
              }}
              alignItems={{
                xs: "center",
                md: "flex-end",
              }}
              justifyContent="space-between"
              spacing={1}
            >
              <Chip
                icon={
                  isFetching ? (
                    <CircularProgress
                      size={15}
                      sx={{ color: "#86efac" }}
                    />
                  ) : (
                    <RefreshIcon />
                  )
                }
                label={TT.autoRefresh}
                sx={{
                  color: "#fff",
                  fontWeight: 750,
                  bgcolor:
                    "rgba(255,255,255,.1)",

                  "& .MuiChip-icon": {
                    color: "#86efac",
                  },
                }}
              />

              <ExportButton
                label={TT.actions.export}
                sx={{
                  minHeight: 40,
                  color: COLORS.navy,
                  bgcolor: "#fff",
                  borderRadius: 2.25,
                  textTransform: "none",
                  fontWeight: 850,

                  "&:hover": {
                    bgcolor: "#f8fafc",
                  },
                }}
              />
            </Stack>
          </Stack>

          <Grid
            container
            spacing={1.25}
            sx={{ mt: 2.5 }}
          >
            <Grid
              item
              xs={6}
              md={3}
              sx={{ display: "flex" }}
            >
              <StatCard
                title={TT.stats.total}
                value={
                  isLoading
                    ? "…"
                    : counts.total
                }
                icon={
                  <AssessmentOutlinedIcon />
                }
                accent="#67e8f9"
                active={current === "ALL"}
              />
            </Grid>

            <Grid
              item
              xs={6}
              md={3}
              sx={{ display: "flex" }}
            >
              <StatCard
                title={TT.stats.new}
                value={
                  isLoading
                    ? "…"
                    : counts.yeni
                }
                icon={
                  <ReportProblemOutlinedIcon />
                }
                accent="#fda4af"
                active={current === "Yeni"}
              />
            </Grid>

            <Grid
              item
              xs={6}
              md={3}
              sx={{ display: "flex" }}
            >
              <StatCard
                title={TT.stats.inreview}
                value={
                  isLoading
                    ? "…"
                    : counts.inc
                }
                icon={<HourglassTopIcon />}
                accent="#fbbf24"
                active={
                  current === "İnceleniyor"
                }
              />
            </Grid>

            <Grid
              item
              xs={6}
              md={3}
              sx={{ display: "flex" }}
            >
              <StatCard
                title={TT.stats.resolved}
                value={
                  isLoading
                    ? "…"
                    : counts.coz
                }
                icon={<TaskAltIcon />}
                accent="#86efac"
                active={current === "Çözüldü"}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box
        sx={{
          px: {
            xs: 1,
            sm: 2,
            md: 3,
          },
          mt: {
            xs: 1.5,
            md: 2,
          },
        }}
      >
        <QuickFilters
          value={current}
          onChange={handleQuick}
          TT={TT}
        />
      </Box>
    </Box>
  );
}

function FeedbackRow({
  record,
  TT,
  onOpenDetail,
}) {
  const category = getCategoryMeta(
    record.kategori,
    TT,
  );

  const status = getStatusMeta(
    record.durum,
    TT,
  );

  const formattedDate = record.tarih
    ? new Date(record.tarih).toLocaleString()
    : "-";

  return (
    <MotionPaper
      layout
      initial={{
        opacity: 0,
        y: 12,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      exit={{
        opacity: 0,
        y: -10,
      }}
      whileHover={{
        y: -2,
        boxShadow:
          "0 14px 32px rgba(15,23,42,.08)",
      }}
      transition={{ duration: 0.25 }}
      variant="outlined"
      sx={{
        p: {
          xs: 1.5,
          md: 1.75,
        },
        borderRadius: 3,
        borderColor: status.border,
        bgcolor: "#fff",
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0,1fr)",
          md: "minmax(175px,.95fr) minmax(230px,1.55fr) minmax(145px,.7fr) minmax(155px,.75fr) auto",
        },
        gap: {
          xs: 1.4,
          md: 2,
        },
        alignItems: "center",
        position: "relative",
        overflow: "hidden",

        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 5,
          bgcolor: status.color,
        },
      }}
    >
      <Box
        minWidth={0}
        sx={{ pl: 0.5 }}
      >
        <Chip
          size="small"
          label={category.label}
          sx={{
            height: 25,
            fontWeight: 850,
            color: category.color,
            bgcolor: category.bg,
          }}
        />

        <Typography
          sx={{
            mt: 0.75,
            fontWeight: 900,
            color: COLORS.text,
            lineHeight: 1.35,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {record.baslik || "-"}
        </Typography>

        <Stack
          direction="row"
          spacing={0.6}
          alignItems="center"
          sx={{ mt: 0.65 }}
        >
          <PersonOutlineIcon
            sx={{
              fontSize: 16,
              color: COLORS.textSoft,
            }}
          />

          <Typography
            variant="caption"
            sx={{
              color: COLORS.textSoft,
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {record.gonderen || "-"}
          </Typography>
        </Stack>
      </Box>

      <Typography
        sx={{
          color: COLORS.textSoft,
          lineHeight: 1.6,
          fontSize: ".9rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
        }}
      >
        {record.aciklama || "-"}
      </Typography>

      <Box>
        <StatusChip
          value={record.durum}
          TT={TT}
          compact
        />
      </Box>

      <Stack
        direction="row"
        spacing={0.7}
        alignItems="center"
      >
        <CalendarMonthOutlinedIcon
          sx={{
            fontSize: 18,
            color: COLORS.textSoft,
          }}
        />

        <Typography
          variant="caption"
          sx={{
            color: COLORS.textSoft,
            fontWeight: 750,
            lineHeight: 1.45,
          }}
        >
          {formattedDate}
        </Typography>
      </Stack>

      <Stack
        direction={{
          xs: "row",
          md: "column",
        }}
        alignItems={{
          xs: "center",
          md: "flex-end",
        }}
        justifyContent="space-between"
        spacing={1}
      >
        {record.resim ? (
          <Tooltip title={TT.fields.image}>
            <Box
              component="img"
              src={record.resim}
              alt={
                record.baslik ||
                TT.fields.image
              }
              sx={{
                width: 54,
                height: 42,
                objectFit: "cover",
                borderRadius: 1.75,
                border: `1px solid ${COLORS.border}`,
              }}
              onError={(event) => {
                event.currentTarget.style.display =
                  "none";
              }}
            />
          </Tooltip>
        ) : (
          <Box />
        )}

        <Button
          size="small"
          variant="contained"
          onClick={() =>
            onOpenDetail(record)
          }
          endIcon={
            <OpenInNewIcon fontSize="small" />
          }
          sx={{
            minHeight: 38,
            borderRadius: 2.25,
            textTransform: "none",
            whiteSpace: "nowrap",
            fontWeight: 850,
            bgcolor: COLORS.navy,
            boxShadow: "none",

            "&:hover": {
              bgcolor: COLORS.navySoft,
              boxShadow: "none",
            },
          }}
        >
          {TT.actions.openDetail}
        </Button>
      </Stack>
    </MotionPaper>
  );
}

function FeedbackSection({
  title,
  items,
  TT,
  onOpenDetail,
  accent,
}) {
  if (!items?.length) {
    return <EmptyState />;
  }

  return (
    <MotionBox
      initial={{
        opacity: 0,
        y: 14,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      transition={{ duration: 0.3 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
        >
          <Box
            sx={{
              width: 9,
              height: 32,
              borderRadius: 10,
              bgcolor: accent,
            }}
          />

          <Typography
            component="h2"
            sx={{
              color: COLORS.text,
              fontSize: {
                xs: "1.05rem",
                sm: "1.25rem",
              },
              fontWeight: 950,
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Chip
          label={items.length}
          sx={{
            fontWeight: 900,
            color: COLORS.text,
            bgcolor: "#e2e8f0",
          }}
        />
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 0,
            md: 1,
          },
          borderRadius: {
            xs: 0,
            md: 3.5,
          },
          borderColor: {
            xs: "transparent",
            md: COLORS.border,
          },
          bgcolor: {
            xs: "transparent",
            md: "rgba(255,255,255,.58)",
          },
        }}
      >
        <Box
          sx={{
            display: {
              xs: "none",
              md: "grid",
            },
            gridTemplateColumns:
              "minmax(175px,.95fr) minmax(230px,1.55fr) minmax(145px,.7fr) minmax(155px,.75fr) auto",
            gap: 2,
            px: 2,
            py: 1.15,
          }}
        >
          {[
            TT.table.subject,
            TT.table.message,
            TT.table.status,
            TT.table.date,
            TT.table.action,
          ].map((label) => (
            <Typography
              key={label}
              variant="caption"
              sx={{
                color: COLORS.textSoft,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: ".045em",

                "&:last-of-type": {
                  textAlign: "right",
                },
              }}
            >
              {label}
            </Typography>
          ))}
        </Box>

        <Stack spacing={1}>
          <AnimatePresence initial={false}>
            {items.map((record) => (
              <FeedbackRow
                key={record.id}
                record={record}
                TT={TT}
                onOpenDetail={onOpenDetail}
              />
            ))}
          </AnimatePresence>
        </Stack>
      </Paper>
    </MotionBox>
  );
}

function CategoryTabs({
  tab,
  setTab,
  dilek,
  sikayet,
  TT,
}) {
  const buttons = [
    {
      id: "dilek",
      label: TT.sections.wishes,
      count: dilek.length,
      badge: dilek.filter(
        (record) => record.durum === "Yeni",
      ).length,
      color: COLORS.blue,
      icon: (
        <AutoAwesomeIcon fontSize="small" />
      ),
    },
    {
      id: "sikayet",
      label: TT.sections.complaints,
      count: sikayet.length,
      badge: sikayet.filter(
        (record) => record.durum === "Yeni",
      ).length,
      color: COLORS.redDark,
      icon: (
        <ReportProblemOutlinedIcon fontSize="small" />
      ),
    },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0.75,
        mb: 2.25,
        borderRadius: 3,
        borderColor: COLORS.border,
        bgcolor: "#fff",
        display: "flex",
        gap: 0.75,
        overflowX: "auto",
      }}
    >
      {buttons.map((item) => {
        const active = tab === item.id;

        return (
          <Badge
            key={item.id}
            badgeContent={item.badge}
            color="error"
            invisible={!item.badge}
            sx={{
              flex: {
                xs: "0 0 auto",
                sm: 1,
              },
              minWidth: {
                xs: 190,
                sm: 0,
              },

              "& .MuiBadge-badge": {
                right: 10,
                top: 10,
                fontWeight: 900,
              },
            }}
          >
            <Button
              fullWidth
              onClick={() =>
                setTab(item.id)
              }
              startIcon={item.icon}
              sx={{
                minHeight: 48,
                px: 2,
                borderRadius: 2.4,
                textTransform: "none",
                justifyContent: "flex-start",
                fontWeight: 900,
                color: active
                  ? "#fff"
                  : item.color,
                bgcolor: active
                  ? item.color
                  : "transparent",

                "&:hover": {
                  bgcolor: active
                    ? item.color
                    : `${item.color}0d`,
                },
              }}
            >
              {item.label} ({item.count})
            </Button>
          </Badge>
        );
      })}
    </Paper>
  );
}

function ListContent({
  tab,
  setTab,
  TT,
}) {
  const { data = [] } = useListContext();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const [update] = useUpdate();

  const dilek = useMemo(
    () =>
      data.filter((record) =>
        ["İstek", "Öneri"].includes(
          record.kategori,
        ),
      ),
    [data],
  );

  const sikayet = useMemo(
    () =>
      data.filter(
        (record) =>
          record.kategori === "Şikayet",
      ),
    [data],
  );

  const onOpenDetail = (record) => {
    if (record.durum === "Yeni") {
      update(
        "geribildirimler",
        {
          id: record.id,
          data: {
            durum: "İnceleniyor",
          },
          previousData: record,
        },
        {
          onSuccess: () => {
            refresh();

            redirect(
              `/geribildirimler/${record.id}`,
            );
          },
        },
      );
    } else {
      redirect(
        `/geribildirimler/${record.id}`,
      );
    }
  };

  return (
    <Box
      sx={{
        px: {
          xs: 1,
          sm: 2,
          md: 3,
        },
        pt: 2,
        pb: 4,
      }}
    >
      <CategoryTabs
        tab={tab}
        setTab={setTab}
        dilek={dilek}
        sikayet={sikayet}
        TT={TT}
      />

      {tab === "dilek" && (
        <FeedbackSection
          title={TT.sections.wishes}
          items={dilek}
          TT={TT}
          onOpenDetail={onOpenDetail}
          accent={COLORS.blue}
        />
      )}

      {tab === "sikayet" && (
        <FeedbackSection
          title={TT.sections.complaints}
          items={sikayet}
          TT={TT}
          onOpenDetail={onOpenDetail}
          accent={COLORS.redDark}
        />
      )}
    </Box>
  );
}

export const GeriBildirimlerPage = (
  props,
) => {
  const { TT } = useChoices();
  const refresh = useRefresh();

  const [tab, setTab] = useState("dilek");

  useEffect(() => {
    const timer = setInterval(
      () => refresh(),
      10000,
    );

    return () => clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    const searchParams = new URLSearchParams(
      window.location.search,
    );

    const requestedTab = (
      searchParams.get("tab") || ""
    ).toLowerCase();

    if (
      ["dilek", "sikayet"].includes(
        requestedTab,
      )
    ) {
      setTab(requestedTab);
    }
  }, []);

  return (
    <List
      {...props}
      title={TT.pageTitle}
      perPage={50}
      sort={{
        field: "tarih",
        order: "DESC",
      }}
      actions={false}
      empty={<EmptyState />}
      sx={{
        width: "100%",
        maxWidth: "none",
        bgcolor: COLORS.page,

        "& .RaList-main": {
          width: "100%",
          maxWidth: "none",
        },

        "& .RaList-content": {
          width: "100%",
          maxWidth: "none",
          m: 0,
          p: 0,
          bgcolor: COLORS.page,
          borderRadius: 0,
          boxShadow: "none",
          overflow: "visible",
        },
      }}
    >
      <GlobalStyles
        styles={{
          html: {
            overflowX: "hidden",
          },
          body: {
            margin: 0,
            overflowX: "hidden",
            background: COLORS.page,
          },
          "*": {
            boxSizing: "border-box",
          },
          ".RaNotification-content": {
            fontWeight: 800,
          },
        }}
      />

      <ListHeader />

      <ListContent
        tab={tab}
        setTab={setTab}
        TT={TT}
      />
    </List>
  );
};

function EditToolbar({ TT }) {
  const redirect = useRedirect();

  return (
    <RaToolbar
      sx={{
        position: "sticky",
        bottom: {
          xs: 8,
          md: 12,
        },
        zIndex: 20,
        mx: {
          xs: 1,
          md: 3,
        },
        mb: 2,
        p: {
          xs: 1.1,
          sm: 1.4,
        },
        minHeight: 0,
        borderRadius: 3,
        border: `1px solid ${COLORS.border}`,
        bgcolor: "rgba(255,255,255,.94)",
        backdropFilter: "blur(14px)",
        boxShadow:
          "0 14px 35px rgba(15,23,42,.14)",
        display: "flex",
        flexDirection: {
          xs: "column",
          sm: "row",
        },
        alignItems: "stretch",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      <DeleteButton
        mutationMode="pessimistic"
        label={TT.replies.delete}
        confirmTitle={
          TT.replies.deleteConfirmTitle
        }
        confirmContent={
          TT.replies.deleteConfirmContent
        }
        sx={{
          minHeight: 44,
          borderRadius: 2.25,
          textTransform: "none",
          fontWeight: 850,
          color: COLORS.redDark,
          bgcolor: COLORS.redSoft,
        }}
      />

      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        spacing={1}
      >
        <Button
          type="button"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            redirect("/geribildirimler")
          }
          sx={{
            minHeight: 44,
            px: 2.25,
            borderRadius: 2.25,
            textTransform: "none",
            fontWeight: 850,
            borderColor: COLORS.border,
            color: COLORS.text,
          }}
        >
          {TT.actions.back}
        </Button>

        <SaveButton
          label={TT.actions.save}
          sx={{
            minHeight: 44,
            px: 2.5,
            borderRadius: 2.25,
            textTransform: "none",
            fontWeight: 900,
            bgcolor: COLORS.navy,
            boxShadow: "none",

            "&:hover": {
              bgcolor: COLORS.navySoft,
              boxShadow: "none",
            },
          }}
        />
      </Stack>
    </RaToolbar>
  );
}

function RepliesPanel({
  TT,
  onFirstReplySetInceleme,
}) {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();

  const [loading, setLoading] =
    useState(false);

  const [sending, setSending] =
    useState(false);

  const [items, setItems] = useState([]);
  const [text, setText] = useState("");

  const load = useCallback(() => {
    if (!record?.id) {
      return;
    }

    setLoading(true);

    dataProvider
      .getList("geribildirim_yanitlari", {
        pagination: {
          page: 1,
          perPage: 100,
        },
        sort: {
          field: "tarih",
          order: "DESC",
        },
        filter: {
          geribildirim_id: record.id,
        },
      })
      .then((response) => {
        setItems(response?.data || []);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }, [dataProvider, record?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    if (!text.trim() || sending) {
      return;
    }

    setSending(true);

    try {
      await dataProvider.create(
        "geribildirim_yanitlari",
        {
          data: {
            geribildirim_id: record.id,
            metin: text.trim(),
          },
        },
      );

      setText("");

      if (
        record?.durum === "Yeni" &&
        typeof onFirstReplySetInceleme ===
          "function"
      ) {
        await onFirstReplySetInceleme(record);
      }

      load();
      refresh();
      notify(TT.replies.sentOk);
    } catch {
      notify(TT.replies.sendErr, {
        type: "warning",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2,
        borderRadius: 3.5,
        borderColor: COLORS.border,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        spacing={1.2}
        alignItems="center"
        sx={{
          p: {
            xs: 1.5,
            sm: 2,
          },
          bgcolor: "#f8fafc",
        }}
      >
        <Avatar
          sx={{
            bgcolor: COLORS.blueSoft,
            color: COLORS.blue,
          }}
        >
          <ChatBubbleOutlineIcon />
        </Avatar>

        <Box>
          <Typography
            sx={{
              color: COLORS.text,
              fontWeight: 900,
            }}
          >
            {TT.replies.title}
          </Typography>

          <Typography
            variant="caption"
            sx={{ color: COLORS.textSoft }}
          >
            {TT.replies.subtitle}
          </Typography>
        </Box>
      </Stack>

      <Divider />

      <Stack
        spacing={1.25}
        sx={{
          minHeight: 150,
          maxHeight: 360,
          overflowY: "auto",
          p: {
            xs: 1.5,
            sm: 2,
          },
          bgcolor: "#fff",
        }}
      >
        {loading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ py: 4 }}
          >
            <CircularProgress size={26} />

            <Typography
              variant="body2"
              sx={{ color: COLORS.textSoft }}
            >
              {TT.replies.loading}
            </Typography>
          </Stack>
        ) : items.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ py: 4 }}
          >
            <ChatBubbleOutlineIcon
              sx={{
                fontSize: 34,
                color: "#cbd5e1",
              }}
            />

            <Typography
              variant="body2"
              sx={{ color: COLORS.textSoft }}
            >
              {TT.replies.none}
            </Typography>
          </Stack>
        ) : (
          <AnimatePresence initial={false}>
            {items.map(
              (message, index) => (
                <MotionBox
                  key={message.id}
                  initial={{
                    opacity: 0,
                    x: 16,
                  }}
                  animate={{
                    opacity: 1,
                    x: 0,
                  }}
                  transition={{
                    duration: 0.22,
                    delay: Math.min(
                      index * 0.025,
                      0.2,
                    ),
                  }}
                  sx={{
                    alignSelf: "flex-end",
                    width: {
                      xs: "94%",
                      sm: "82%",
                    },
                    p: 1.5,
                    borderRadius:
                      "16px 16px 4px 16px",
                    bgcolor: COLORS.blueSoft,
                    border:
                      "1px solid #bfdbfe",
                  }}
                >
                  <Typography
                    sx={{
                      color: COLORS.text,
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.6,
                      fontSize: ".92rem",
                    }}
                  >
                    {message.metin}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.75,
                      display: "block",
                      textAlign: "right",
                      color: COLORS.textSoft,
                      fontWeight: 700,
                    }}
                  >
                    {message.tarih
                      ? new Date(
                          message.tarih,
                        ).toLocaleString()
                      : "-"}
                  </Typography>
                </MotionBox>
              ),
            )}
          </AnimatePresence>
        )}
      </Stack>

      <Divider />

      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        spacing={1}
        alignItems="stretch"
        sx={{
          p: {
            xs: 1.25,
            sm: 1.5,
          },
          bgcolor: "#f8fafc",
        }}
      >
        <TextFieldMUI
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          placeholder={
            TT.replies.placeholder
          }
          value={text}
          onChange={(event) =>
            setText(event.target.value)
          }
          onKeyDown={(event) => {
            if (
              (event.ctrlKey ||
                event.metaKey) &&
              event.key === "Enter"
            ) {
              event.preventDefault();
              send();
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              bgcolor: "#fff",
            },
          }}
        />

        <Button
          type="button"
          variant="contained"
          disabled={!text.trim() || sending}
          onClick={send}
          endIcon={
            sending ? (
              <CircularProgress
                size={17}
                color="inherit"
              />
            ) : (
              <SendRoundedIcon />
            )
          }
          sx={{
            minWidth: {
              sm: 150,
            },
            minHeight: 48,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 900,
            bgcolor: COLORS.blue,
            boxShadow: "none",

            "&:hover": {
              bgcolor: "#1d4ed8",
              boxShadow: "none",
            },
          }}
        >
          {TT.actions.send}
        </Button>
      </Stack>
    </Paper>
  );
}

function EditHero({ TT, record }) {
  return (
    <Box
      sx={{
        color: "#fff",
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(125deg, #06172d 0%, #123d70 55%, #075985 100%)",
        borderBottom: `4px solid ${COLORS.orange}`,
        px: {
          xs: 1.5,
          sm: 2.5,
          md: 3.5,
        },
        py: {
          xs: 3,
          md: 4,
        },
      }}
    >
      <Stack
        direction={{
          xs: "column",
          md: "row",
        }}
        alignItems={{
          xs: "flex-start",
          md: "center",
        }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box sx={{ maxWidth: 760 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: {
                xs: "1.75rem",
                sm: "2.35rem",
                md: "2.8rem",
              },
              lineHeight: 1.08,
              fontWeight: 950,
            }}
          >
            {TT.detailTitle}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              color:
                "rgba(255,255,255,.72)",
              lineHeight: 1.6,
            }}
          >
            {TT.detailSubtitle}
          </Typography>
        </Box>

        {record?.durum && (
          <StatusChip
            value={record.durum}
            TT={TT}
          />
        )}
      </Stack>
    </Box>
  );
}

function GeriBildirimEditInner({
  TT,
  DURUMLAR,
  KATEGORILER,
}) {
  const record = useRecordContext();
  const refresh = useRefresh();
  const [update] = useUpdate();

  const makeIncelemeIfNew = async (
    currentRecord,
  ) => {
    const selectedRecord =
      currentRecord || record;

    if (
      !selectedRecord?.id ||
      selectedRecord?.durum !== "Yeni"
    ) {
      return;
    }

    await update(
      "geribildirimler",
      {
        id: selectedRecord.id,
        data: {
          durum: "İnceleniyor",
        },
        previousData: selectedRecord,
      },
      {},
    );

    refresh();
  };

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: COLORS.page,
        pb: 1,
      }}
    >
      <EditHero
        TT={TT}
        record={record}
      />

      <SimpleForm
        toolbar={<EditToolbar TT={TT} />}
        sx={{
          width: "100%",
          maxWidth: "none",
          p: 0,

          "& .MuiCardContent-root": {
            p: 0,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            px: {
              xs: 1,
              sm: 2,
              md: 3,
            },
            py: 2.5,
          }}
        >
          <Grid
            container
            spacing={2}
            alignItems="stretch"
          >
            <Grid
              item
              xs={12}
              lg={8}
              sx={{ display: "flex" }}
            >
              <Paper
                variant="outlined"
                sx={{
                  width: "100%",
                  p: {
                    xs: 1.5,
                    sm: 2.25,
                  },
                  borderRadius: 3.5,
                  borderColor: COLORS.border,
                  bgcolor: "#fff",
                  boxShadow:
                    "0 12px 32px rgba(15,23,42,.05)",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.2}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Avatar
                    sx={{
                      bgcolor:
                        COLORS.blueSoft,
                      color: COLORS.blue,
                    }}
                  >
                    <PersonOutlineIcon />
                  </Avatar>

                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: COLORS.text,
                      }}
                    >
                      {TT.fields.title}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          COLORS.textSoft,
                      }}
                    >
                      {record?.gonderen ||
                        TT.fields.sender}
                    </Typography>
                  </Box>
                </Stack>

                <TextInput
                  source="baslik"
                  label={TT.fields.title}
                  disabled
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root":
                      {
                        borderRadius: 2.5,
                        bgcolor: "#f8fafc",
                      },
                  }}
                />

                <Grid
                  container
                  spacing={1.5}
                >
                  <Grid
                    item
                    xs={12}
                    sm={6}
                  >
                    <TextInput
                      source="gonderen"
                      label={
                        TT.fields.sender
                      }
                      disabled
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root":
                          {
                            borderRadius: 2.5,
                            bgcolor:
                              "#f8fafc",
                          },
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                  >
                    <SelectInput
                      source="kategori"
                      label={
                        TT.fields.category
                      }
                      choices={KATEGORILER}
                      disabled
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root":
                          {
                            borderRadius: 2.5,
                            bgcolor:
                              "#f8fafc",
                          },
                      }}
                    />
                  </Grid>
                </Grid>

                <TextInput
                  source="aciklama"
                  label={TT.fields.desc}
                  multiline
                  minRows={5}
                  disabled
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root":
                      {
                        borderRadius: 2.5,
                        bgcolor: "#f8fafc",
                      },

                    "& textarea": {
                      lineHeight: 1.65,
                    },
                  }}
                />

                <RepliesPanel
                  TT={TT}
                  onFirstReplySetInceleme={
                    makeIncelemeIfNew
                  }
                />
              </Paper>
            </Grid>

            <Grid
              item
              xs={12}
              lg={4}
              sx={{ display: "flex" }}
            >
              <Stack
                spacing={2}
                sx={{ width: "100%" }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: {
                      xs: 1.5,
                      sm: 2,
                    },
                    borderRadius: 3.5,
                    borderColor:
                      COLORS.border,
                    bgcolor: "#fff",
                    boxShadow:
                      "0 12px 32px rgba(15,23,42,.05)",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1.5 }}
                  >
                    <CheckCircleOutlineIcon
                      sx={{
                        color:
                          COLORS.greenDark,
                      }}
                    />

                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: COLORS.text,
                      }}
                    >
                      {TT.fields.status}
                    </Typography>
                  </Stack>

                  <SelectInput
                    source="durum"
                    label={TT.fields.status}
                    choices={DURUMLAR}
                    fullWidth
                    sx={{
                      mb: 0,

                      "& .MuiOutlinedInput-root":
                        {
                          borderRadius: 2.5,
                        },
                    }}
                  />
                </Paper>

                <Paper
                  variant="outlined"
                  sx={{
                    p: {
                      xs: 1.5,
                      sm: 2,
                    },
                    borderRadius: 3.5,
                    borderColor:
                      COLORS.border,
                    bgcolor: "#fff",
                    boxShadow:
                      "0 12px 32px rgba(15,23,42,.05)",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1.5 }}
                  >
                    <AutoAwesomeIcon
                      sx={{
                        color: COLORS.violet,
                      }}
                    />

                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: COLORS.text,
                      }}
                    >
                      {TT.fields.image}
                    </Typography>
                  </Stack>

                  {record?.resim ? (
                    <ImageField
                      source="resim"
                      sx={{
                        display: "block",

                        "& img": {
                          display: "block",
                          width: "100%",
                          maxHeight: 420,
                          objectFit: "contain",
                          borderRadius: 2.5,
                          bgcolor: "#f8fafc",
                          border: `1px solid ${COLORS.border}`,
                        },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        minHeight: 180,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: 2.5,
                        border:
                          "1px dashed #cbd5e1",
                        bgcolor: "#f8fafc",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            COLORS.textSoft,
                        }}
                      >
                        {TT.empty.noImage}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </SimpleForm>
    </Box>
  );
}

export const GeriBildirimEdit = (props) => {
  const {
    DURUMLAR,
    KATEGORILER,
    TT,
  } = useChoices();

  const notify = useNotify();
  const redirect = useRedirect();

  const onSuccess = () => {
    notify(TT.toastUpdated);
    redirect("/geribildirimler");
  };

  const onError = (error) => {
    const message =
      error?.message || TT.errorGeneric;

    notify(`❌ ${message}`, {
      type: "warning",
    });
  };

  return (
    <Edit
      {...props}
      title={TT.detailTitle}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess,
        onError,
      }}
      sx={{
        width: "100%",
        maxWidth: "none",
        bgcolor: COLORS.page,

        "& .RaEdit-main": {
          width: "100%",
          maxWidth: "none",
          m: 0,
        },

        "& .RaEdit-card": {
          borderRadius: 0,
          boxShadow: "none",
          overflow: "visible",
        },
      }}
    >
      <GlobalStyles
        styles={{
          html: {
            overflowX: "hidden",
          },
          body: {
            margin: 0,
            overflowX: "hidden",
            background: COLORS.page,
          },
          "*": {
            boxSizing: "border-box",
          },
          ".RaNotification-content": {
            fontWeight: 800,
          },
        }}
      />

      <GeriBildirimEditInner
        TT={TT}
        DURUMLAR={DURUMLAR}
        KATEGORILER={KATEGORILER}
      />
    </Edit>
  );
};

export default GeriBildirimlerPage;